import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

export const SavingsCalculator: React.FC = () => {
  const [timeshareValue, setTimeshareValue] = useState('')
  const [monthlyFees, setMonthlyFees] = useState('')
  const [yearsOwned, setYearsOwned] = useState('')
  const [results, setResults] = useState({
    attorneyFees: 0,
    totalCostWithAttorney: 0,
    diyGuidePrice: 997,
    totalSavings: 0,
    percentageSaved: 0
  })

  const calculateSavings = () => {
    const value = parseFloat(timeshareValue) || 0
    const years = parseFloat(yearsOwned) || 0

    // Attorney fees typically range from $5,000 to $15,000
    // We'll use a formula based on timeshare value and complexity
    const baseAttorneyFee = Math.max(5000, Math.min(15000, value * 0.15))
    const complexityMultiplier = Math.min(2, 1 + (years * 0.05)) // More years = more complexity
    const attorneyFees = Math.round(baseAttorneyFee * complexityMultiplier)
    
    const totalCostWithAttorney = attorneyFees
    const diyGuidePrice = 997
    const totalSavings = totalCostWithAttorney - diyGuidePrice
    const percentageSaved = totalCostWithAttorney > 0 ? (totalSavings / totalCostWithAttorney) * 100 : 0

    setResults({
      attorneyFees,
      totalCostWithAttorney,
      diyGuidePrice,
      totalSavings,
      percentageSaved
    })
  }

  useEffect(() => {
    if (timeshareValue || monthlyFees || yearsOwned) {
      calculateSavings()
    }
  }, [timeshareValue, monthlyFees, yearsOwned])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Calculate Your Potential Savings
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
            See exactly how much you could save by using our DIY guide instead of hiring expensive attorneys.
            Most people save between $8,000 - $14,000!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Calculator Input */}
          <div className="bg-gray-50 rounded-lg p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Enter Your Timeshare Details
            </h3>
            
            <div className="space-y-6">
              <div>
                <label htmlFor="timeshare-value" className="block text-sm font-medium text-gray-700 mb-2">
                  Original Purchase Price
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    id="timeshare-value"
                    className="block w-full pl-7 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="50,000"
                    value={timeshareValue}
                    onChange={(e) => setTimeshareValue(e.target.value)}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  The amount you originally paid for your timeshare
                </p>
              </div>

              <div>
                <label htmlFor="monthly-fees" className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Maintenance Fees
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    id="monthly-fees"
                    className="block w-full pl-7 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="800"
                    value={monthlyFees}
                    onChange={(e) => setMonthlyFees(e.target.value)}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Your current monthly maintenance fees
                </p>
              </div>

              <div>
                <label htmlFor="years-owned" className="block text-sm font-medium text-gray-700 mb-2">
                  Years Owned
                </label>
                <input
                  type="number"
                  id="years-owned"
                  className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="5"
                  value={yearsOwned}
                  onChange={(e) => setYearsOwned(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  How long you've owned your timeshare
                </p>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Your Potential Savings
            </h3>

            <div className="space-y-6">
              {/* Attorney Cost */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Typical Attorney Fees</span>
                  <span className="text-lg font-bold text-red-600">
                    {formatCurrency(results.attorneyFees)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Based on industry averages ($5K-$15K+)
                </p>
              </div>

              {/* DIY Guide Cost */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Our DIY Guide</span>
                  <span className="text-lg font-bold text-green-600">
                    {formatCurrency(results.diyGuidePrice)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  One-time purchase, lifetime access
                </p>
              </div>

              {/* Total Savings */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2">
                    {formatCurrency(results.totalSavings)}
                  </div>
                  <div className="text-green-100 text-sm">
                    Total Savings ({results.percentageSaved.toFixed(1)}% less)
                  </div>
                </div>
              </div>

              {/* Additional Benefits */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-medium text-gray-900 mb-2">Additional Benefits</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Immediate access - start today
                  </li>
                  <li className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    No hourly billing or hidden fees
                  </li>
                  <li className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Complete control over your case
                  </li>
                  <li className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Money-back guarantee
                  </li>
                </ul>
              </div>

              {results.totalSavings > 0 && (
                <div className="text-center mt-6">
                  <Link 
                    to="/register"
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-lg transition-colors no-underline"
                  >
                    Get Your Guide Now - Save {formatCurrency(results.totalSavings)}
                  </Link>
                  <p className="text-xs text-gray-600 mt-2">
                    Join thousands who've successfully exited their timeshares
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500 max-w-4xl mx-auto">
            * Estimates based on industry averages and typical attorney fees. Actual savings may vary. 
            Attorney fees can range from $5,000 to $15,000+ depending on case complexity. 
            Our calculations use conservative estimates based on timeshare value and ownership duration.
          </p>
        </div>
      </div>
    </div>
  )
}